import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import {
  Container,
  Title,
  Text,
  Group,
  TextInput,
  Table,
  Badge,
  Skeleton,
  Pagination,
  Stack,
  Paper,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { IconSearch, IconBuildingStore, IconTag } from '@tabler/icons-react';
import { useCompaniesPaginated } from '@api/companies';
import SetDefaultCostCenterModal from '@components/SetDefaultCostCenterModal/SetDefaultCostCenterModal';
import { useAuth } from '@providers/auth';

function Companies() {
  const { session } = useAuth();
  const costCentersAvailable = !!session.activeAccount?.cost_centers_available;
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = Number(searchParams.get('page')) || 1;
  const [activePage, setActivePage] = useState(initialPage);
  const [taxIdInput, setTaxIdInput] = useState('');
  const [legalNameInput, setLegalNameInput] = useState('');
  const [debouncedTaxId] = useDebouncedValue(taxIdInput, 300);
  const [debouncedLegalName] = useDebouncedValue(legalNameInput, 300);

  const [openedModal, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [selectedCompany, setSelectedCompany] = useState<{
    id: string;
    name: string;
    defaultCostCenterId: number | null;
  } | null>(null);

  const { data, isFetching } = useCompaniesPaginated({
    page: activePage,
    taxId: debouncedTaxId,
    legalName: debouncedLegalName,
    view: 'clients',
  });

  const handlePageChange = (page: number) => {
    setActivePage(page);
    if (page === 1) {
      searchParams.delete('page');
    } else {
      searchParams.set('page', String(page));
    }
    setSearchParams(searchParams);
  };

  const handleFilterChange = () => {
    setActivePage(1);
    searchParams.delete('page');
    setSearchParams(searchParams);
  };

  const handleOpenCostCenterModal = (company: { id: string; uuid: string; legal_name: string; default_cost_center?: { id: number; name: string } | null }) => {
    setSelectedCompany({
      id: company.uuid as string,
      name: company.legal_name as string,
      defaultCostCenterId: (company.default_cost_center as { id: number } | null)?.id ?? null,
    });
    openModal();
  };

  const companies = data?.data ?? [];
  const totalPages = data?.meta?.pages ?? 1;
  const totalCount = data?.meta?.total ?? 0;

  return (
    <Container fluid pb="xl">
      <Stack gap="lg">
        <div>
          <Title order={1}>Empresas</Title>
          <Text c="dimmed">Clientes y proveedores asociados a tus documentos.</Text>
        </div>

        <Paper withBorder radius="md" p="md">
          <Group gap="sm" mb="md">
            <TextInput
              leftSection={<IconSearch size={16} />}
              placeholder="Buscar por RUT..."
              value={taxIdInput}
              onChange={(e) => {
                setTaxIdInput(e.currentTarget.value);
                handleFilterChange();
              }}
              maw={220}
            />
            <TextInput
              leftSection={<IconSearch size={16} />}
              placeholder="Buscar por nombre..."
              value={legalNameInput}
              onChange={(e) => {
                setLegalNameInput(e.currentTarget.value);
                handleFilterChange();
              }}
              maw={280}
            />
            {totalCount > 0 && (
              <Text fz="sm" c="dimmed" ml="auto">
                {totalCount} empresa{totalCount !== 1 ? 's' : ''}
              </Text>
            )}
          </Group>

          <Table horizontalSpacing="md" verticalSpacing="sm" highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>RUT</Table.Th>
                <Table.Th>Razón Social</Table.Th>
                <Table.Th>Actividad</Table.Th>
                <Table.Th>Dirección</Table.Th>
                <Table.Th ta="center">Emitidos</Table.Th>
                <Table.Th ta="center">Recibidos</Table.Th>
                {costCentersAvailable && <Table.Th>Plan de cuenta</Table.Th>}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isFetching ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <Table.Tr key={i}>
                    <Table.Td><Skeleton height={20} /></Table.Td>
                    <Table.Td><Skeleton height={20} /></Table.Td>
                    <Table.Td><Skeleton height={20} /></Table.Td>
                    <Table.Td><Skeleton height={20} /></Table.Td>
                    <Table.Td><Skeleton height={20} /></Table.Td>
                    <Table.Td><Skeleton height={20} /></Table.Td>
                    {costCentersAvailable && <Table.Td><Skeleton height={20} /></Table.Td>}
                  </Table.Tr>
                ))
              ) : companies.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={costCentersAvailable ? 7 : 6}>
                    <Stack align="center" py="xl" gap="xs">
                      <IconBuildingStore size={32} color="var(--mantine-color-dimmed)" />
                      <Text c="dimmed" fz="sm">
                        No se encontraron empresas
                      </Text>
                    </Stack>
                  </Table.Td>
                </Table.Tr>
              ) : (
                companies.map((company) => {
                  const defaultCostCenter = company.default_cost_center as { id: number; name: string } | null | undefined;
                  return (
                    <Table.Tr key={company.id as string}>
                      <Table.Td>
                        <Text fz="sm" ff="monospace">
                          {company.tax_id as string}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text fz="sm" fw={500}>
                          {company.legal_name as string}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text fz="sm" c={company.activity ? undefined : 'dimmed'}>
                          {(company.activity as string) ?? '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text fz="sm" c={company.address ? undefined : 'dimmed'}>
                          {company.address
                            ? `${company.address as string}${company.commune ? `, ${company.commune as string}` : ''}`
                            : '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="center">
                        {(company.sent_count as number) > 0 ? (
                          <Badge variant="light" color="teal" size="sm">
                            {company.sent_count as number}
                          </Badge>
                        ) : (
                          <Text fz="sm" c="dimmed">—</Text>
                        )}
                      </Table.Td>
                      <Table.Td ta="center">
                        {(company.received_count as number) > 0 ? (
                          <Badge variant="light" color="blue" size="sm">
                            {company.received_count as number}
                          </Badge>
                        ) : (
                          <Text fz="sm" c="dimmed">—</Text>
                        )}
                      </Table.Td>
                      {costCentersAvailable && (
                        <Table.Td>
                          <Group gap="xs" wrap="nowrap">
                            {defaultCostCenter ? (
                              <Tooltip label={defaultCostCenter.name} withArrow position="top" openDelay={300}>
                                <Badge variant="light" color="violet" size="sm" maw={160} style={{ overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'default' }}>
                                  {defaultCostCenter.name}
                                </Badge>
                              </Tooltip>
                            ) : (
                              <Text fz="sm" c="dimmed">—</Text>
                            )}
                            <Tooltip label="Asignar plan de cuenta por defecto" withArrow>
                              <ActionIcon
                                variant="subtle"
                                size="sm"
                                color="gray"
                                onClick={() => handleOpenCostCenterModal(company as never)}
                              >
                                <IconTag size={14} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      )}
                    </Table.Tr>
                  );
                })
              )}
            </Table.Tbody>
          </Table>

          <Pagination
            total={totalPages}
            value={activePage}
            onChange={handlePageChange}
            mt="md"
          />
        </Paper>
      </Stack>

      {selectedCompany && (
        <SetDefaultCostCenterModal
          opened={openedModal}
          close={closeModal}
          companyId={selectedCompany.id}
          companyName={selectedCompany.name}
          currentCostCenterId={selectedCompany.defaultCostCenterId}
        />
      )}
    </Container>
  );
}

export default Companies;
